import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  RadioGroup,
  Radio,
  FormControlLabel,
  FormControl,
  FormLabel,
  Alert,
  CircularProgress,
  Divider,
  Chip,
  Card,
  CardContent,
  Autocomplete,
  IconButton,
  Stepper,
  Step,
  StepLabel,
} from '@mui/material';
import {
  Close as CloseIcon,
  CheckCircle as CheckCircleIcon,
  SwapHoriz as SwapHorizIcon,
  PersonAdd as PersonAddIcon,
  Block as BlockIcon,
  TaskAlt as TaskAltIcon,
} from '@mui/icons-material';
import { gebizAPI } from '../../services/api';

const formatCurrency = (value, currency = 'SGD') => {
  if (value === null || value === undefined) return '-';
  return new Intl.NumberFormat('en-SG', {
    style: 'currency',
    currency: currency,
  }).format(value);
};

// Steps for the review process
const steps = ['Select Action', 'Confirm', 'Complete'];

const FuzzyReviewDialog = ({ open, onClose, award, jobId }) => {
  const queryClient = useQueryClient();
  const [activeStep, setActiveStep] = useState(0);
  const [action, setAction] = useState('confirm');
  const [remarks, setRemarks] = useState('');
  const [selectedLeadId, setSelectedLeadId] = useState(null);
  const [selectedLeadName, setSelectedLeadName] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [error, setError] = useState(null);
  const [successData, setSuccessData] = useState(null);

  // Search leads for assign_existing action
  const { data: searchResults, isLoading: isSearching } = useQuery(
    ['leadSearch', searchQuery],
    () => gebizAPI.searchLeads(searchQuery, 20),
    {
      enabled: searchQuery.length >= 2,
      keepPreviousData: true,
    }
  );

  // Review mutation
  const reviewMutation = useMutation(
    (reviewData) => gebizAPI.reviewAward(jobId, award.id, reviewData),
    {
      onSuccess: (data) => {
        queryClient.invalidateQueries(['gebizAwards', jobId]);
        queryClient.invalidateQueries(['gebizSummary', jobId]);
        setSuccessData(data);
        setActiveStep(2); // Move to Complete step
      },
      onError: (err) => {
        setError(err.response?.data?.detail || err.message || 'Failed to submit review');
        setActiveStep(0); // Go back to first step on error
      },
    }
  );

  const handleNext = () => {
    setError(null);
    
    if (activeStep === 0) {
      // Validation before moving to confirmation
      if (action === 'assign_existing' && !selectedLeadId) {
        setError('Please select a lead to assign');
        return;
      }
      setActiveStep(1); // Move to confirmation step
    } else if (activeStep === 1) {
      // Submit the review
      const reviewData = {
        action,
        remarks: remarks || undefined,
      };

      if (action === 'assign_existing') {
        reviewData.lead_id = selectedLeadId;
      }

      reviewMutation.mutate(reviewData);
    }
  };

  const handleBack = () => {
    setError(null);
    setActiveStep(0);
  };

  const handleClose = () => {
    setActiveStep(0);
    setAction('confirm');
    setRemarks('');
    setSelectedLeadId(null);
    setSelectedLeadName('');
    setSearchQuery('');
    setError(null);
    setSuccessData(null);
    onClose();
  };

  if (!award) return null;

  const actionDescriptions = {
    confirm: 'Confirm the current match is correct',
    assign_existing: 'Assign to a different existing lead',
    create_new: 'Create a new lead for this company',
    skip: 'Skip this award (remove lead assignment)',
  };

  // Get action label for display
  const getActionLabel = () => {
    switch (action) {
      case 'confirm':
        return 'Confirm Match';
      case 'assign_existing':
        return `Assign to ${selectedLeadName || 'selected lead'}`;
      case 'create_new':
        return `Create New Lead for "${award?.awarded_to_company}"`;
      case 'skip':
        return 'Skip this Award';
      default:
        return action;
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6">Review Award Match</Typography>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        {/* Stepper */}
        <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {/* Step 0: Select Action */}
        {activeStep === 0 && (
          <>
            {/* Award Details Card */}
            <Card sx={{ mb: 3, bgcolor: 'grey.50' }}>
              <CardContent sx={{ py: 2 }}>
                <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                  Award Details
                </Typography>
                <Typography variant="h6" gutterBottom>
                  {award.awarded_to_company}
                </Typography>
                <Typography variant="body2" color="textSecondary" gutterBottom>
                  {award.title}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
                  <Chip label={formatCurrency(award.award_value, award.award_currency)} size="small" />
                  <Chip label={award.agency_awarding} size="small" variant="outlined" />
                </Box>
              </CardContent>
            </Card>

            {/* Current Match Info */}
            {award.lead_id && (
              <Card sx={{ mb: 3, borderLeft: 4, borderColor: 'warning.main' }}>
                <CardContent sx={{ py: 2 }}>
                  <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                    Suggested Match
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box>
                      <Typography variant="body1" fontWeight="medium">
                        {award.lead_company_name || award.matched_company_name}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        Lead ID: {award.lead_id}
                      </Typography>
                    </Box>
                    {award.match_confidence && (
                      <Chip 
                        label={`${parseFloat(award.match_confidence).toFixed(1)}% confidence`}
                        color={award.match_confidence >= 90 ? 'success' : 'warning'}
                        size="small"
                      />
                    )}
                  </Box>
                </CardContent>
              </Card>
            )}

            <Divider sx={{ my: 2 }} />

            {/* Action Selection */}
            <FormControl component="fieldset" sx={{ width: '100%' }}>
              <FormLabel component="legend">Select Action</FormLabel>
              <RadioGroup
                value={action}
                onChange={(e) => setAction(e.target.value)}
              >
                <FormControlLabel
                  value="confirm"
                  control={<Radio />}
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CheckCircleIcon color="success" fontSize="small" />
                      <Box>
                        <Typography variant="body1">Confirm Match</Typography>
                        <Typography variant="caption" color="textSecondary">
                          {actionDescriptions.confirm}
                        </Typography>
                      </Box>
                    </Box>
                  }
                  disabled={!award.lead_id}
                />
                
                <FormControlLabel
                  value="assign_existing"
                  control={<Radio />}
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <SwapHorizIcon color="primary" fontSize="small" />
                      <Box>
                        <Typography variant="body1">Assign to Different Lead</Typography>
                        <Typography variant="caption" color="textSecondary">
                          {actionDescriptions.assign_existing}
                        </Typography>
                      </Box>
                    </Box>
                  }
                />
                
                <FormControlLabel
                  value="create_new"
                  control={<Radio />}
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <PersonAddIcon color="info" fontSize="small" />
                      <Box>
                        <Typography variant="body1">Create New Lead</Typography>
                        <Typography variant="caption" color="textSecondary">
                          {actionDescriptions.create_new}
                        </Typography>
                      </Box>
                    </Box>
                  }
                />
                
                <FormControlLabel
                  value="skip"
                  control={<Radio />}
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <BlockIcon color="error" fontSize="small" />
                      <Box>
                        <Typography variant="body1">Skip Award</Typography>
                        <Typography variant="caption" color="textSecondary">
                          {actionDescriptions.skip}
                        </Typography>
                      </Box>
                    </Box>
                  }
                />
              </RadioGroup>
            </FormControl>

            {/* Lead Search for assign_existing */}
            {action === 'assign_existing' && (
              <Box sx={{ mt: 3 }}>
                <Autocomplete
                  options={searchResults || []}
                  getOptionLabel={(option) => `${option.company_name} (ID: ${option.id})`}
                  loading={isSearching}
                  onInputChange={(_, value) => setSearchQuery(value)}
                  onChange={(_, value) => {
                    setSelectedLeadId(value?.id || null);
                    setSelectedLeadName(value?.company_name || '');
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Search for Lead"
                      placeholder="Type company name to search..."
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {isSearching ? <CircularProgress size={20} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                  renderOption={(props, option) => (
                    <li {...props}>
                      <Box>
                        <Typography variant="body1">{option.company_name}</Typography>
                        <Typography variant="caption" color="textSecondary">
                          ID: {option.id} | Job: {option.job_id}
                        </Typography>
                      </Box>
                    </li>
                  )}
                />
              </Box>
            )}

            {/* Remarks */}
            <TextField
              fullWidth
              multiline
              rows={2}
              label="Remarks (optional)"
              placeholder="Add any notes about this review decision..."
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
              sx={{ mt: 3 }}
            />
          </>
        )}

        {/* Step 1: Confirmation */}
        {activeStep === 1 && (
          <Box sx={{ textAlign: 'center', py: 2 }}>
            <Typography variant="h6" gutterBottom>
              Confirm Your Action
            </Typography>
            
            <Card sx={{ mb: 3, bgcolor: 'info.50', textAlign: 'left' }}>
              <CardContent>
                <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                  You are about to:
                </Typography>
                <Typography variant="h6" color="primary" gutterBottom>
                  {getActionLabel()}
                </Typography>
                
                <Divider sx={{ my: 2 }} />
                
                <Typography variant="body2" color="textSecondary">
                  <strong>Award:</strong> {award.awarded_to_company}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  <strong>Value:</strong> {formatCurrency(award.award_value, award.award_currency)}
                </Typography>
                {remarks && (
                  <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                    <strong>Remarks:</strong> {remarks}
                  </Typography>
                )}
              </CardContent>
            </Card>
            
            <Alert severity="warning" sx={{ textAlign: 'left' }}>
              This action will modify the database. Are you sure you want to proceed?
            </Alert>
          </Box>
        )}

        {/* Step 2: Complete (Success) */}
        {activeStep === 2 && successData && (
          <Box sx={{ textAlign: 'center', py: 3 }}>
            <TaskAltIcon sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
            
            <Typography variant="h5" color="success.main" gutterBottom>
              Review Completed!
            </Typography>
            
            <Typography variant="body1" color="textSecondary" gutterBottom>
              {successData.message}
            </Typography>
            
            <Card sx={{ mt: 3, textAlign: 'left' }}>
              <CardContent>
                <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                  Updated Award Details
                </Typography>
                <Typography variant="body2">
                  <strong>Award ID:</strong> {successData.award?.id}
                </Typography>
                <Typography variant="body2">
                  <strong>New Status:</strong>{' '}
                  <Chip 
                    label={successData.award?.match_status} 
                    size="small" 
                    color="success"
                  />
                </Typography>
                {successData.award?.lead_id && (
                  <Typography variant="body2">
                    <strong>Linked Lead:</strong> {successData.award?.lead_company_name} (ID: {successData.award?.lead_id})
                  </Typography>
                )}
                {successData.award?.admin_remarks && (
                  <Typography variant="body2">
                    <strong>Remarks:</strong> {successData.award?.admin_remarks}
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Box>
        )}

        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        {activeStep === 0 && (
          <>
            <Button onClick={handleClose}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleNext}
              disabled={action === 'assign_existing' && !selectedLeadId}
            >
              Next
            </Button>
          </>
        )}
        
        {activeStep === 1 && (
          <>
            <Button onClick={handleBack} disabled={reviewMutation.isLoading}>
              Back
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleNext}
              disabled={reviewMutation.isLoading}
            >
              {reviewMutation.isLoading ? (
                <CircularProgress size={24} />
              ) : (
                'Confirm & Submit'
              )}
            </Button>
          </>
        )}
        
        {activeStep === 2 && (
          <Button variant="contained" onClick={handleClose}>
            Close
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default FuzzyReviewDialog;

